import { Link } from "react-router-dom";
import { ShieldCheck, Building2, Users, Stethoscope, Syringe, CalendarCheck, Lock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const AUDIENCES = [
  {
    icon: Building2,
    title: "Health Facilities",
    body: "Nursing homes, assisted living communities, hospitals and clinics rely on us to run seamless on-site immunization drives. We coordinate scheduling, staffing and documentation so your team can stay focused on care — not logistics.",
  },
  {
    icon: Users,
    title: "Organizations & Employers",
    body: "Schools, workplaces, non-profits and community groups can offer flu shots, COVID boosters and routine vaccinations right where your people already are. One point of contact, transparent reporting, and a partner who shows up.",
  },
  {
    icon: Stethoscope,
    title: "Individual Health Professionals",
    body: "Physicians, nurse practitioners and case managers can refer patients, order drives, and manage rosters and insurance details in a secure portal built for clinical workflows — with the compliance safeguards you expect.",
  },
];

const BENEFITS = [
  { icon: Syringe, title: "On-site vaccine drives", body: "We bring the clinic to you — staff, supplies and scheduling handled end to end." },
  { icon: CalendarCheck, title: "Effortless coordination", body: "Request, confirm and reschedule drives in a few clicks, with instant email updates." },
  { icon: Lock, title: "HIPAA-compliant portal", body: "Patient rosters, dates of birth and insurance information stay protected and access-controlled." },
];

export default function HealthPartnerInfo() {
  return (
    <div data-testid="partner-info-page">
      {/* Hero */}
      <section className="mx-auto max-w-5xl px-6 pt-16 pb-10 text-center sm:pt-20">
        <span className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
          <ShieldCheck className="h-4 w-4" /> Health Partner Program
        </span>
        <h1 className="mt-6 font-heading text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
          Partner with Crystal Pharmacy
        </h1>
        <p className="mx-auto mt-5 max-w-2xl text-base text-muted-foreground sm:text-lg">
          On-site vaccine drives and a secure, HIPAA-compliant portal built for health facilities, organizations, and individual health professionals — so protecting your people is simple, coordinated, and reliable.
        </p>

        {/* CTA button — under title + short description, above the longer description */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button asChild size="lg" className="rounded-full px-8" data-testid="partner-info-portal-btn">
            <Link to="/health-partners">Access the Health Partner Portal <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="rounded-full px-8" data-testid="partner-info-request-btn">
            <Link to="/vaccine-drive">Request a Vaccine Drive</Link>
          </Button>
        </div>
      </section>

      {/* Longer description */}
      <section className="mx-auto max-w-3xl px-6 py-8">
        <div className="space-y-5 text-base leading-relaxed text-muted-foreground">
          <p>
            For over a decade, Crystal Pharmacy has been a trusted immunization provider for the Raton community and beyond. Our Health Partner Program extends that same clinical expertise directly to the facilities and professionals who care for others — combining pharmacist-led vaccination services with a modern portal that removes the paperwork and guesswork from every drive.
          </p>
          <p>
            When you partner with us, you get more than a vaccine supplier. You get a dedicated clinical team that plans each drive around your schedule, verifies eligibility and insurance in advance, and handles the reporting your organization needs for compliance and peace of mind. Whether you're vaccinating forty staff members or hundreds of residents, we scale to meet you — and we show up prepared.
          </p>
          <p>
            Behind it all is a HIPAA-compliant Health Partner Portal where you can submit and manage vaccine drive requests, build patient rosters with dates of birth and primary or secondary insurance, and update the details of any upcoming drive at any time. Signing in is effortless and password-free: we email you a secure one-tap link (or a one-time code) each time, so there's nothing to remember and nothing to leak.
          </p>
          <p>
            Ready to make immunizations easier for the people who count on you? Create your Health Partner account today — it takes less than a minute, and our team will be in touch to confirm your first drive.
          </p>
        </div>
      </section>

      {/* Audiences */}
      <section className="mx-auto max-w-6xl px-6 py-10">
        <h2 className="text-center font-heading text-2xl font-bold sm:text-3xl">Who we partner with</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {AUDIENCES.map((a) => (
            <div key={a.title} className="rounded-2xl border border-border bg-card p-6" data-testid={`partner-audience-${a.title.split(" ")[0].toLowerCase()}`}>
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary"><a.icon className="h-6 w-6" /></div>
              <h3 className="mt-4 font-heading text-lg font-bold">{a.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{a.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="mx-auto max-w-6xl px-6 py-10">
        <div className="grid gap-6 sm:grid-cols-3">
          {BENEFITS.map((b) => (
            <div key={b.title} className="flex gap-4 rounded-2xl bg-secondary/40 p-6">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-primary"><b.icon className="h-5 w-5" /></div>
              <div><h3 className="font-heading text-base font-bold">{b.title}</h3><p className="mt-1 text-sm text-muted-foreground">{b.body}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="mx-auto max-w-4xl px-6 py-16 text-center">
        <div className="rounded-3xl bg-primary px-8 py-12 text-primary-foreground">
          <h2 className="font-heading text-3xl font-bold">Become a Health Partner</h2>
          <p className="mx-auto mt-3 max-w-xl text-teal-50">Join the facilities and professionals who trust Crystal Pharmacy to keep their communities protected.</p>
          <Button asChild size="lg" variant="secondary" className="mt-6 rounded-full px-8" data-testid="partner-info-cta-btn">
            <Link to="/health-partners">Get started in the portal <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
